'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/comments/styles.css';
import { IconButton } from 'BomdisoftComponents/buttons/icon';
import { insertMention, matchPeople, mentionedIn, mentionQueryAt, splitMentions } from 'BomdisoftComponents/comments/mentions';
import { type CommentsComment, type CommentsComposer, type CommentsFilter, type CommentsFilterOption, type CommentsPerson, type CommentsThread, type CommentsWords, earlierBreak, REACTION_EMOJI, VISIBLE_BEFORE_FOLD, visibleThreads } from 'BomdisoftComponents/comments/render';
import { AddRoundedIcon } from 'BomdisoftComponents/icons/add-rounded';
import { MoreVertRoundedIcon } from 'BomdisoftComponents/icons/more-vert-rounded';
import { TextField } from 'BomdisoftComponents/inputs/field/text';
import { ToggleButton, ToggleGroup } from 'BomdisoftComponents/inputs/toggle-group/index';
import { Menu, MenuItem } from 'BomdisoftComponents/menu/index';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { type FormEvent, type KeyboardEvent, type ReactNode, useId, useRef, useState } from 'react';

export type { CommentsAnchor, CommentsComment, CommentsComposer, CommentsFilter, CommentsFilterOption, CommentsPerson, CommentsReaction, CommentsThread, CommentsWords } from 'BomdisoftComponents/comments/render';
export { REACTION_EMOJI, VISIBLE_BEFORE_FOLD } from 'BomdisoftComponents/comments/render';

export type CommentsProps = {
  canReact?: boolean;
  canResolve?: boolean;
  canWrite?: boolean;
  composer: CommentsComposer;
  empty?: (filter: CommentsFilter) => ReactNode;
  filters: readonly CommentsFilterOption[];
  findPeople?: (query: string) => Promise<readonly CommentsPerson[]>;
  id?: string;
  onDelete: (commentId: string) => Promise<void>;
  onLoadEarlier?: (threadId: string) => Promise<void>;
  onEdit: (commentId: string, body: string, mentions: readonly string[]) => Promise<void>;
  onFailure: (error: unknown) => void;
  onReact: (commentId: string, emoji: string) => Promise<void>;
  onReopen: (threadId: string) => Promise<void>;
  onReply: (threadId: string, body: string, mentions: readonly string[]) => Promise<void>;
  onResolve: (threadId: string) => Promise<void>;
  onStart: (body: string, mentions: readonly string[]) => Promise<void>;
  onUnreact: (commentId: string, emoji: string) => Promise<void>;
  people?: readonly CommentsPerson[];
  testIdPrefix: string;
  threads: readonly CommentsThread[];
  words: CommentsWords;
};

type Anchor = { current: HTMLElement | null };

type Mention = { active: number; input: HTMLInputElement; part: string; query: string; start: number };

type Found = { people: readonly CommentsPerson[]; query: string };

type FieldOptions = {
  autoFocus?: boolean;
  className?: string;
  defaultValue?: string;
  mentioned?: readonly CommentsPerson[];
  onSubmit: (body: string, mentions: readonly string[], input: HTMLInputElement) => void;
  part: string;
  placeholder?: string;
  testId: string;
};

const isFilter = (value: string | null): value is CommentsFilter => value === 'open' || value === 'resolved' || value === 'unread';

export const Comments = ({ canReact = true, canResolve = true, canWrite = true, composer, empty, filters, findPeople, id, onDelete, onLoadEarlier, onEdit, onFailure, onReact, onReopen, onReply, onResolve, onStart, onUnreact, people = [], testIdPrefix, threads, words }: CommentsProps) => {
  const generated = useId();
  const base = id ?? generated;
  const isReacting = canWrite && canReact;
  const idOf = (part: string) => `${base}-${part}`;
  const [filter, setFilter] = useState<CommentsFilter>('open');
  const [expanded, setExpanded] = useState<readonly string[]>([]);
  const [loadingEarlier, setLoadingEarlier] = useState<readonly string[]>([]);

  const loadEarlier = (threadId: string) => {
    if (onLoadEarlier === undefined || loadingEarlier.includes(threadId)) return;
    setLoadingEarlier(current => [...current, threadId]);
    onLoadEarlier(threadId)
      .catch(onFailure)
      .finally(() => setLoadingEarlier(current => current.filter(held => held !== threadId)));
  };
  const [editing, setEditing] = useState<string | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [openPicker, setOpenPicker] = useState<string | null>(null);
  const [mention, setMention] = useState<Mention | null>(null);
  const [found, setFound] = useState<Found | null>(null);
  const asked = useRef(0);
  const known = useRef(new Map<string, CommentsPerson>());
  const anchors = useRef(new Map<string, Anchor>());
  const anchorOf = (key: string) => {
    const known = anchors.current.get(key);
    if (known !== undefined) return known;
    const created: Anchor = { current: null };
    anchors.current.set(key, created);
    return created;
  };
  const listAnchor = anchorOf('people');
  const offersMentions = findPeople !== undefined || people.length > 0;
  const shownFor = (query: string): readonly CommentsPerson[] => {
    if (findPeople === undefined) return matchPeople(people, query);
    if (found === null) return [];
    return found.query === query ? found.people : matchPeople(found.people, query);
  };
  const matches = mention === null ? [] : shownFor(mention.query);
  const highlighted = mention === null ? undefined : matches[mention.active];
  const report = (action: Promise<void>) => {
    action.catch(onFailure);
  };

  const ask = (search: (query: string) => Promise<readonly CommentsPerson[]>, query: string) => {
    asked.current += 1;
    const ticket = asked.current;
    void search(query).then(
      answer => {
        if (ticket !== asked.current) return;
        for (const person of answer) known.current.set(person.id, person);
        setFound({ people: answer, query });
        setMention(current => (current === null ? null : { ...current, active: 0 }));
      },
      (error: unknown) => {
        if (ticket !== asked.current) return;
        setFound({ people: [], query });
        onFailure(error);
      },
    );
  };

  const openMention = (input: HTMLInputElement, part: string, start: number, query: string) => {
    listAnchor.current = input.closest<HTMLElement>('.bomdisoft-field-box') ?? input;
    setMention({ active: 0, input, part, query, start });
    if (findPeople !== undefined) ask(findPeople, query);
  };

  const pick = (person: CommentsPerson) => {
    if (mention === null) return;
    const { input, start } = mention;
    const caret = input.selectionStart ?? input.value.length;
    const edit = insertMention(input.value, caret, start, person.name);
    input.value = edit.text;
    input.focus();
    input.setSelectionRange(edit.caret, edit.caret);
    setMention(null);
  };

  const handleInput = (part: string) => (event: FormEvent<HTMLInputElement>) => {
    if (!offersMentions) return;
    const input = event.currentTarget;
    const typed = mentionQueryAt(input.value, input.selectionStart ?? input.value.length);
    if (typed === null || (findPeople === undefined && matchPeople(people, typed.query).length === 0)) {
      setMention(null);
      return;
    }
    openMention(input, part, typed.start, typed.query);
  };

  const handleKeyDown = (part: string, onSubmit: FieldOptions['onSubmit'], mentioned: readonly CommentsPerson[]) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (mention !== null && mention.part === part && matches.length > 0) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        setMention({ ...mention, active: (mention.active + step + matches.length) % matches.length });
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const chosen = matches[mention.active];
        if (chosen !== undefined) pick(chosen);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        setMention(null);
        return;
      }
    }
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const input = event.currentTarget;
    onSubmit(input.value.trim(), mentionedIn(input.value, [...new Map([...people, ...known.current.values(), ...mentioned].map(person => [person.id, person])).values()]), input);
  };

  const renderField = ({ autoFocus, className, defaultValue, mentioned = [], onSubmit, part, placeholder, testId }: FieldOptions) => (
    <TextField
      aria-activedescendant={mention?.part === part && highlighted !== undefined ? idOf(`person-${highlighted.id}`) : undefined}
      autoFocus={autoFocus}
      className={className}
      data-testid={testId}
      defaultValue={defaultValue}
      endAdornment={
        offersMentions ? (
          <Tooltip title={words.mention ?? ''}>
            <button
              aria-expanded={mention?.part === part && matches.length > 0}
              aria-haspopup='listbox'
              aria-label={words.mention ?? ''}
              className='bomdisoft-comments-mention-button'
              data-testid={`${testId}-mention`}
              onClick={event => {
                const input = event.currentTarget.closest('.bomdisoft-field-box')?.querySelector('input') ?? null;
                if (input === null) return;
                if (mention?.part === part) {
                  setMention(null);
                  return;
                }
                openMention(input, part, input.selectionStart ?? input.value.length, '');
              }}
              type='button'
            >
              @
            </button>
          </Tooltip>
        ) : undefined
      }
      id={idOf(part)}
      onInput={handleInput(part)}
      onKeyDown={handleKeyDown(part, onSubmit, mentioned)}
      placeholder={placeholder}
    />
  );

  const submitStart: FieldOptions['onSubmit'] = (body, mentions, input) => {
    if (body === '') return;
    onStart(body, mentions)
      .then(() => {
        input.value = '';
      })
      .catch(onFailure);
  };

  const renderBody = (comment: CommentsComment) =>
    splitMentions(
      comment.body,
      (comment.mentions ?? []).map(person => person.name),
    ).map(part =>
      part.isMention ? (
        <span className='bomdisoft-comments-mention' key={part.start}>
          {part.text}
        </span>
      ) : (
        part.text
      ),
    );

  const renderComment = (comment: CommentsComment) => {
    const commentId = comment.id;
    const canChange = canWrite && comment.canChange !== false;
    const content =
      editing === commentId ? (
        renderField({
          autoFocus: true,
          defaultValue: comment.body,
          mentioned: comment.mentions,
          onSubmit: (body, mentions) => {
            if (body === '') {
              setEditing(null);
              return;
            }
            onEdit(commentId, body, mentions)
              .then(() => setEditing(null))
              .catch(onFailure);
          },
          part: `edit-${commentId}`,
          testId: `${testIdPrefix}-comment-edit-input-${commentId}`,
        })
      ) : comment.isDeleted ? (
        <p className='bomdisoft-comments-deleted'>{words.deleted}</p>
      ) : (
        <p className='bomdisoft-comments-text'>{renderBody(comment)}</p>
      );
    return (
      <div className='bomdisoft-comments-comment' data-testid={`${testIdPrefix}-comment-${commentId}`} key={commentId}>
        <span aria-hidden='true' className='bomdisoft-comments-avatar'>
          {comment.authorInitials}
        </span>
        <div className='bomdisoft-comments-body'>
          <span className='bomdisoft-comments-who'>
            <b>{comment.authorLabel}</b>
            {` ${comment.when}`}
            {canChange ? (
              <Tooltip title={words.menu}>
                <IconButton
                  aria-label={words.menu}
                  className='bomdisoft-comments-menu-button'
                  data-testid={`${testIdPrefix}-comment-menu-${commentId}`}
                  onClick={() => setOpenMenu(current => (current === commentId ? null : commentId))}
                  ref={node => {
                    anchorOf(`menu-${commentId}`).current = node;
                  }}
                  size='small'
                >
                  <MoreVertRoundedIcon size={12} />
                </IconButton>
              </Tooltip>
            ) : null}
          </span>
          {content}
          {comment.isDeleted || (!isReacting && comment.reactions.length === 0) ? null : !isReacting ? (
            <span className='bomdisoft-comments-reactions'>
              {comment.reactions.map((reaction, index) => (
                <span className='bomdisoft-comments-reaction' data-mine={reaction.isMine ? '' : undefined} data-testid={`${testIdPrefix}-reaction-${commentId}-${index}`} key={reaction.emoji}>
                  {`${reaction.emoji} ${reaction.count}`}
                </span>
              ))}
            </span>
          ) : (
            <span className='bomdisoft-comments-reactions'>
              {comment.reactions.map((reaction, index) => (
                <button
                  aria-pressed={reaction.isMine}
                  className='bomdisoft-comments-reaction'
                  data-mine={reaction.isMine ? '' : undefined}
                  data-testid={`${testIdPrefix}-reaction-${commentId}-${index}`}
                  key={reaction.emoji}
                  onClick={() => report(reaction.isMine ? onUnreact(commentId, reaction.emoji) : onReact(commentId, reaction.emoji))}
                  type='button'
                >
                  {`${reaction.emoji} ${reaction.count}`}
                </button>
              ))}
              <Tooltip title={words.addReaction}>
                <button
                  aria-label={words.addReaction}
                  className='bomdisoft-comments-reaction'
                  data-testid={`${testIdPrefix}-add-reaction-${commentId}`}
                  onClick={() => setOpenPicker(current => (current === commentId ? null : commentId))}
                  ref={node => {
                    anchorOf(`react-${commentId}`).current = node;
                  }}
                  type='button'
                >
                  <AddRoundedIcon size={12} />
                </button>
              </Tooltip>
            </span>
          )}
        </div>
        {canChange ? (
          <Menu anchorRef={anchorOf(`menu-${commentId}`)} id={idOf(`menu-${commentId}`)} onClose={() => setOpenMenu(null)} open={openMenu === commentId}>
            <MenuItem
              onClick={() => {
                setOpenMenu(null);
                setEditing(commentId);
              }}
              testId={`${testIdPrefix}-comment-edit-${commentId}`}
            >
              {words.editComment}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setOpenMenu(null);
                report(onDelete(commentId));
              }}
              testId={`${testIdPrefix}-comment-delete-${commentId}`}
              variant='danger'
            >
              {words.deleteComment}
            </MenuItem>
          </Menu>
        ) : null}
        {isReacting ? (
          <Menu anchorRef={anchorOf(`react-${commentId}`)} id={idOf(`picker-${commentId}`)} label={words.addReaction} onClose={() => setOpenPicker(null)} open={openPicker === commentId} role='dialog'>
            <span className='bomdisoft-comments-picker'>
              {REACTION_EMOJI.map(emoji => (
                <button
                  aria-label={emoji}
                  className='bomdisoft-comments-pick'
                  data-testid={`${testIdPrefix}-pick-emoji-${emoji}`}
                  key={emoji}
                  onClick={() => {
                    setOpenPicker(null);
                    report(onReact(commentId, emoji));
                  }}
                  type='button'
                >
                  {emoji}
                </button>
              ))}
            </span>
          </Menu>
        ) : null}
      </div>
    );
  };

  const renderThread = (thread: CommentsThread) => {
    const paged = thread.hiddenCount !== undefined;
    const hidden = earlierBreak(thread);
    const isLoading = loadingEarlier.includes(thread.id);
    const shown = paged || expanded.includes(thread.id) ? thread.comments : thread.comments.slice(0, VISIBLE_BEFORE_FOLD);
    const folded = paged ? 0 : thread.comments.length - shown.length;
    const [first, ...rest] = shown;
    return (
      <div className='bomdisoft-comments-thread' data-resolved={thread.isResolved ? '' : undefined} data-testid={`${testIdPrefix}-thread-${thread.id}`} key={thread.id}>
        {thread.anchor === undefined ? null : (
          <span className='bomdisoft-comments-anchor' data-off={thread.anchor.isOff ? '' : undefined} data-testid={`${testIdPrefix}-thread-anchor-${thread.id}`}>
            {thread.anchor.text}
          </span>
        )}
        {paged ? (
          <>
            {first === undefined ? null : renderComment(first)}
            {hidden > 0 ? (
              <div className='bomdisoft-comments-break'>
                <button aria-busy={isLoading ? true : undefined} className='bomdisoft-comments-break-chip' data-testid={`${testIdPrefix}-thread-earlier-${thread.id}`} disabled={isLoading} onClick={() => loadEarlier(thread.id)} type='button'>
                  {words.showEarlier?.(hidden) ?? words.showMore(hidden)}
                </button>
              </div>
            ) : null}
            {rest.map(renderComment)}
          </>
        ) : (
          shown.map(renderComment)
        )}
        {folded > 0 ? (
          <button className='bomdisoft-comments-more' data-testid={`${testIdPrefix}-thread-more-${thread.id}`} onClick={() => setExpanded(current => [...current, thread.id])} type='button'>
            {words.showMore(folded)}
          </button>
        ) : null}
        {canWrite && canResolve ? (
          <div className='bomdisoft-comments-foot'>
            <button className='bomdisoft-comments-action' data-testid={`${testIdPrefix}-thread-resolve-${thread.id}`} onClick={() => report(thread.isResolved ? onReopen(thread.id) : onResolve(thread.id))} type='button'>
              {thread.isResolved ? words.reopen : words.resolve}
            </button>
          </div>
        ) : null}
        {canWrite
          ? renderField({
              onSubmit: (body, mentions, input) => {
                if (body === '') return;
                onReply(thread.id, body, mentions)
                  .then(() => {
                    input.value = '';
                  })
                  .catch(onFailure);
              },
              part: `reply-${thread.id}`,
              placeholder: words.reply,
              testId: `${testIdPrefix}-thread-reply-${thread.id}`,
            })
          : null}
      </div>
    );
  };

  const composerField = canWrite ? renderField({ className: 'bomdisoft-comments-composer', onSubmit: submitStart, part: 'composer', placeholder: words.newComment, testId: `${testIdPrefix}-comment-composer` }) : null;
  const listed = visibleThreads(threads, filter);

  return (
    <div className='bomdisoft-comments'>
      {composer === 'single' ? null : (
        <ToggleGroup aria-label={words.filterLabel} className='bomdisoft-comments-filter' onChange={next => setFilter(isFilter(next) ? next : 'open')} shape='pill' value={filter}>
          {filters.map(({ disabledReason, value }) => {
            const label = words.filters[value];
            const button = (
              <ToggleButton aria-label={disabledReason === undefined ? undefined : `${label}. ${disabledReason}`} data-testid={`${testIdPrefix}-seg-${value}`} disabled={disabledReason !== undefined} key={value} value={value}>
                {label}
              </ToggleButton>
            );
            return disabledReason === undefined ? (
              button
            ) : (
              <Tooltip key={value} title={disabledReason}>
                {button}
              </Tooltip>
            );
          })}
        </ToggleGroup>
      )}
      {composer === 'top' ? composerField : null}
      <div className='bomdisoft-comments-threads'>{listed.length === 0 ? (empty?.(filter) ?? null) : listed.map(renderThread)}</div>
      {composer === 'foot' || (composer === 'single' && threads.length === 0) ? composerField : null}
      {canWrite && offersMentions ? (
        <Menu anchorRef={listAnchor} className='bomdisoft-comments-people' id={idOf('people')} label={words.mention} matchAnchorWidth onClose={() => setMention(null)} open={mention !== null && matches.length > 0} role='listbox'>
          {(mention === null ? people : matches).map((person, index) => (
            <MenuItem id={idOf(`person-${person.id}`)} key={person.id} onClick={() => pick(person)} selected={mention !== null && index === mention.active} testId={`${testIdPrefix}-person-${person.id}`}>
              <span aria-hidden='true' className='bomdisoft-comments-person-mark'>
                {person.initials}
              </span>
              <span className='bomdisoft-comments-person-name'>{person.name}</span>
            </MenuItem>
          ))}
        </Menu>
      ) : null}
    </div>
  );
};
